if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoomLevel = 1;
        this.onZoomRangeInput = this.onZoomRangeInput.bind(this);
        this.onZoomIn = this.onZoomIn.bind(this);
        this.onZoomOut = this.onZoomOut.bind(this);
        this.onPrev = this.onPrev.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onThumbClick = this.onThumbClick.bind(this);
        this.onViewportPointerDown = this.onViewportPointerDown.bind(this);
        this.onViewportPointerMove = this.onViewportPointerMove.bind(this);
        this.onViewportPointerUp = this.onViewportPointerUp.bind(this);
        this.stopZoomEventBubble = this.stopZoomEventBubble.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.dataset.viewerLayout === 'true' && !this.viewerBound) {
          this.bindViewerControls();
          this.viewerBound = true;
        }
      }

      bindViewerControls() {
        this.zoomRange = this.querySelector('[data-zoom-range]');
        this.zoomInBtn = this.querySelector('[data-zoom-in]');
        this.zoomOutBtn = this.querySelector('[data-zoom-out]');
        this.prevBtn = this.querySelector('[data-modal-prev]');
        this.nextBtn = this.querySelector('[data-modal-next]');
        this.thumbs = this.querySelector('[data-modal-thumbs]');
        this.viewport = this.querySelector('[data-modal-viewport]');
        this.zoomControl = this.querySelector('[data-zoom-control]');

        this.zoomRange?.addEventListener('input', this.onZoomRangeInput);
        this.zoomRange?.addEventListener('change', this.onZoomRangeInput);
        this.zoomInBtn?.addEventListener('click', this.onZoomIn);
        this.zoomOutBtn?.addEventListener('click', this.onZoomOut);
        this.prevBtn?.addEventListener('click', this.onPrev);
        this.nextBtn?.addEventListener('click', this.onNext);
        this.thumbs?.addEventListener('click', this.onThumbClick);

        // evita conflito com pan/pointer do viewport e do media-modal
        this.zoomControl?.addEventListener('pointerdown', this.stopZoomEventBubble);
        this.zoomControl?.addEventListener('pointerup', this.stopZoomEventBubble);
        this.zoomControl?.addEventListener('touchstart', this.stopZoomEventBubble, { passive: true });

        this.viewport?.addEventListener('pointerdown', this.onViewportPointerDown);
        this.viewport?.addEventListener('pointermove', this.onViewportPointerMove);
        this.viewport?.addEventListener('pointerup', this.onViewportPointerUp);
        this.viewport?.addEventListener('pointercancel', this.onViewportPointerUp);
        this.viewport?.addEventListener('pointerleave', this.onViewportPointerUp);
      }

      stopZoomEventBubble(event) {
        event.stopPropagation();
      }

      hide() {
        this.resetZoom();
        this.pauseModalVideos();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        if (this.dataset.viewerLayout === 'true') {
          this.resetZoom();
          this.syncThumbs();
          this.playThumbVideos();
        }
      }

      getActiveMedia() {
        return this.querySelector(
          '.product-media-modal__content [data-media-id].active, .product-media-modal__content .active[data-media-id], .product-media-modal__content .active'
        );
      }

      pauseModalVideos() {
        this.querySelectorAll('.product-media-modal__content video, [data-modal-thumbs] video').forEach((video) => {
          video.pause();
        });
      }

      playThumbVideos() {
        this.querySelectorAll('[data-modal-thumbs] video').forEach((video) => {
          this.prepareModalVideo(video);
        });
      }

      prepareModalVideo(video) {
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.autoplay = true;
        video.playsInline = true;
        video.setAttribute('autoplay', '');
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.loop = true;
        video.setAttribute('loop', '');
        video.controls = false;
        video.removeAttribute('controls');

        const tryPlay = () => {
          video.muted = true;
          const playPromise = video.play();
          if (playPromise?.catch) playPromise.catch(() => {});
        };

        tryPlay();
        if (video.readyState < 2) {
          video.addEventListener('loadeddata', tryPlay, { once: true });
          video.addEventListener('canplay', tryPlay, { once: true });
        }
      }

      ensureDeferredMediaLoaded(deferred) {
        if (!deferred) return null;

        let video = deferred.querySelector('video');
        if (video) return video;

        if (typeof deferred.loadContent === 'function') {
          deferred.loadContent(false, false);
          video = deferred.querySelector('video');
          if (video) return video;
        }

        const template = deferred.querySelector('template');
        const sourceVideo = template?.content?.querySelector('video');
        if (!sourceVideo) return null;

        video = sourceVideo.cloneNode(true);
        deferred.setAttribute('loaded', true);
        deferred.appendChild(video);
        return video;
      }

      playActiveVideo() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        const activeEl = content.querySelector('[data-media-id].active, .active[data-media-id], .active');
        if (!activeEl) return;

        // não pausar vídeos das miniaturas — ficam em loop o tempo todo
        content.querySelectorAll('video').forEach((video) => {
          if (video.closest('[data-modal-thumbs]')) return;
          const isActive = activeEl === video || activeEl.contains(video);
          if (!isActive) {
            video.pause();
            try {
              video.currentTime = 0;
            } catch (e) {}
          }
        });

        // vídeo nativo já no DOM (product-media-modal__video)
        if (activeEl.matches?.('[data-modal-video], .product-media-modal__video')) {
          const video = activeEl.querySelector('video');
          if (video) this.prepareModalVideo(video);
          return;
        }

        const deferred =
          activeEl.nodeName === 'DEFERRED-MEDIA'
            ? activeEl
            : activeEl.querySelector?.('deferred-media');

        if (deferred) {
          const video = this.ensureDeferredMediaLoaded(deferred);
          if (video) this.prepareModalVideo(video);
          return;
        }

        const directVideo = activeEl.nodeName === 'VIDEO' ? activeEl : activeEl.querySelector?.('video');
        if (directVideo) this.prepareModalVideo(directVideo);
      }

      showActiveMedia() {
        const openerId = this.openedBy?.getAttribute('data-media-id');
        this.querySelectorAll(
          '.product-media-modal__content [data-media-id], .product-media-modal__content .active'
        ).forEach((element) => {
          element.classList.remove('active');
        });

        const activeMedia =
          this.querySelector(`.product-media-modal__content [data-media-id="${openerId}"]`) ||
          this.querySelector('.product-media-modal__content [data-media-id]');

        if (!activeMedia) return;

        activeMedia.classList.add('active');
        activeMedia.closest?.('.product-media-modal__model')?.classList.add('active');

        if (this.dataset.viewerLayout !== 'true') {
          activeMedia.scrollIntoView();
          const container = this.querySelector('[role="document"]');
          if (container && activeMedia.width) {
            container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
          }
        }

        this.syncThumbs();
        this.playActiveVideo();
      }

      setActiveByMediaId(mediaId) {
        if (!mediaId) return;
        this.querySelectorAll('.product-media-modal__content [data-media-id], .product-media-modal__content .active').forEach(
          (el) => {
            el.classList.remove('active');
          }
        );
        const next = this.querySelector(`.product-media-modal__content [data-media-id="${mediaId}"]`);
        if (!next) return;
        next.classList.add('active');
        next.closest?.('.product-media-modal__model')?.classList.add('active');
        this.resetZoom();
        this.syncThumbs();
        // play no mesmo tick do clique (gesto do usuário / iOS)
        this.playActiveVideo();
      }

      syncThumbs() {
        if (!this.thumbs) return;
        const active = this.querySelector('.product-media-modal__content [data-media-id].active');
        const activeId = active?.getAttribute('data-media-id');
        this.thumbs.querySelectorAll('[data-thumb-media-id]').forEach((thumb) => {
          const isActive = thumb.getAttribute('data-thumb-media-id') === activeId;
          if (isActive) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });

        const currentThumb = this.thumbs.querySelector('[aria-current="true"]');
        currentThumb?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }

      getOrderedMediaIds() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return [];
        return Array.from(content.querySelectorAll(':scope > [data-media-id], :scope > .product-media-modal__model')).reduce(
          (ids, el) => {
            const id =
              el.getAttribute('data-media-id') || el.querySelector?.('[data-media-id]')?.getAttribute('data-media-id');
            if (id && !ids.includes(id)) ids.push(id);
            return ids;
          },
          []
        );
      }

      stepMedia(delta) {
        const ids = this.getOrderedMediaIds();
        if (ids.length < 2) return;
        const active = this.querySelector('.product-media-modal__content [data-media-id].active');
        const currentId = active?.getAttribute('data-media-id');
        const index = Math.max(0, ids.indexOf(currentId));
        const nextIndex = (index + delta + ids.length) % ids.length;
        this.setActiveByMediaId(ids[nextIndex]);
      }

      onPrev(event) {
        event.preventDefault();
        event.stopPropagation();
        this.stepMedia(-1);
      }

      onNext(event) {
        event.preventDefault();
        event.stopPropagation();
        this.stepMedia(1);
      }

      onThumbClick(event) {
        const thumb = event.target.closest('[data-thumb-media-id]');
        if (!thumb) return;
        event.preventDefault();
        event.stopPropagation();
        this.setActiveByMediaId(thumb.getAttribute('data-thumb-media-id'));
      }

      applyZoom() {
        const active = this.getActiveMedia();
        if (!active) return;

        const isHostedMedia =
          active.nodeName === 'DEFERRED-MEDIA' ||
          active.classList.contains('product-media-modal__model') ||
          active.classList.contains('product-media-modal__video') ||
          active.hasAttribute('data-modal-video');

        if (this.zoomLevel <= 1.01) {
          active.style.transform = '';
          active.style.maxWidth = '';
          active.style.width = '';
          active.style.maxHeight = '';
          active.style.height = '';
        } else if (isHostedMedia) {
          // vídeo/modelo: scale (width% não funciona bem com o box fixo)
          active.style.width = '';
          active.style.maxWidth = '100%';
          active.style.maxHeight = 'calc(100dvh - 16rem)';
          active.style.height = '';
          active.style.transform = `scale(${this.zoomLevel})`;
        } else {
          active.style.maxWidth = 'none';
          active.style.maxHeight = 'none';
          active.style.width = `${Math.round(100 * this.zoomLevel)}%`;
          active.style.height = 'auto';
          active.style.transform = '';
        }

        if (this.viewport) {
          this.viewport.classList.toggle('is-zoomed', this.zoomLevel > 1.01);
          if (this.zoomLevel <= 1.01) {
            this.viewport.scrollLeft = 0;
            this.viewport.scrollTop = 0;
          }
        }
        if (this.zoomRange) this.zoomRange.value = String(this.zoomLevel);
      }

      resetZoom() {
        this.zoomLevel = 1;
        this.isPanning = false;
        this.querySelectorAll('.product-media-modal__content [data-media-id], .product-media-modal__content .active').forEach(
          (el) => {
            el.style.transform = '';
            el.style.maxWidth = '';
            el.style.width = '';
            el.style.maxHeight = '';
            el.style.height = '';
          }
        );
        if (this.viewport) {
          this.viewport.classList.remove('is-zoomed');
          this.viewport.scrollLeft = 0;
          this.viewport.scrollTop = 0;
        }
        if (this.zoomRange) this.zoomRange.value = '1';
      }

      onZoomRangeInput(event) {
        event.stopPropagation();
        this.zoomLevel = Number(event.target.value) || 1;
        this.applyZoom();
      }

      onZoomIn(event) {
        event.preventDefault();
        event.stopPropagation();
        this.zoomLevel = Math.min(3, Math.round((this.zoomLevel + 0.25) * 100) / 100);
        this.applyZoom();
      }

      onZoomOut(event) {
        event.preventDefault();
        event.stopPropagation();
        this.zoomLevel = Math.max(1, Math.round((this.zoomLevel - 0.25) * 100) / 100);
        this.applyZoom();
      }

      onViewportPointerDown(event) {
        if (this.zoomLevel <= 1.01) return;
        if (event.target.closest('button, input, a, [data-zoom-control]')) return;
        this.isPanning = true;
        this.panStartX = event.clientX;
        this.panStartY = event.clientY;
        this.scrollStartLeft = this.viewport.scrollLeft;
        this.scrollStartTop = this.viewport.scrollTop;
        this.viewport.setPointerCapture?.(event.pointerId);
      }

      onViewportPointerMove(event) {
        if (!this.isPanning || !this.viewport) return;
        const dx = event.clientX - this.panStartX;
        const dy = event.clientY - this.panStartY;
        this.viewport.scrollLeft = this.scrollStartLeft - dx;
        this.viewport.scrollTop = this.scrollStartTop - dy;
      }

      onViewportPointerUp() {
        this.isPanning = false;
      }
    }
  );
}
